import { Check } from "lucide-react";
import type { ChangeEvent, CSSProperties, JSX, ReactNode, Ref } from "react";
import { useState } from "react";
import { cx } from "../cx";

export interface CheckboxProps {
  label?: ReactNode | undefined;
  /** Controlled checked state. */
  checked?: boolean | undefined;
  /** Uncontrolled initial state. */
  defaultChecked?: boolean | undefined;
  onChange?: ((checked: boolean) => void) | undefined;
  disabled?: boolean | undefined;
  name?: string | undefined;
  value?: string | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
  ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * Checkbox — blue-800 fill, volt check, backed by a real input. Controlled
 * or uncontrolled. Ported from design/tarski-kit/components/forms/Checkbox.jsx.
 */
export function Checkbox({
  label,
  checked,
  defaultChecked = false,
  onChange,
  disabled = false,
  name,
  value,
  className,
  style,
  ref,
}: CheckboxProps): JSX.Element {
  const [internal, setInternal] = useState(defaultChecked);
  const isOn = checked !== undefined ? checked : internal;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.currentTarget.checked;
    if (checked === undefined) {
      setInternal(next);
    }
    onChange?.(next);
  };

  return (
    <label
      className={cx("tk-checkbox", disabled && "tk-checkbox--disabled", className)}
      style={style}
    >
      <input
        ref={ref}
        type="checkbox"
        className="tk-checkbox__input"
        checked={isOn}
        onChange={handleChange}
        disabled={disabled}
        name={name}
        value={value}
      />
      <span className="tk-checkbox__box" aria-hidden="true">
        <Check size={12} strokeWidth={3} className="tk-checkbox__mark" aria-hidden="true" />
      </span>
      {label === undefined ? null : <span className="tk-checkbox__label">{label}</span>}
    </label>
  );
}
